import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { Button } from '@/ui/components/common/Button'
import { CookiePreferencesDialog } from '@/ui/components/common/CookiePreferencesDialog'
import { useConsentStore, selectHasRespondedAndValid } from '@/application/stores/consent.store'

/**
 * Bottom cookie-consent banner, GDPR/CNIL-compliant.
 *
 * ## Why this is not a blocking modal
 *
 * CNIL guidance (and the spirit of GDPR "freely given" consent) is that a
 * cookie banner must not force a binary choice before the visitor can read
 * anything: the underlying page must stay usable and readable while the
 * banner is up. So this is rendered as a non-modal, fixed-position `region`
 * with `aria-live="polite"` rather than a `role="dialog" aria-modal="true"`
 * overlay — it does not trap focus and does not block pointer interaction
 * with the rest of the page.
 *
 * The "Customize" button opens `CookiePreferencesDialog`, which *is* a real
 * focus-trapped modal — once someone has chosen to fine-tune categories,
 * trapping focus in that dedicated panel is expected and doesn't block their
 * first read of the page.
 *
 * Fixed/sticky positioning (not inline in flow) avoids any layout shift on
 * the pages behind it.
 *
 * `PublicFooter` is never pinned — it always just ends the document in
 * normal flow — so this banner is a fixed bottom sheet at every viewport
 * size, with no variant that switches it out of `fixed`.
 */
export function CookieConsentBanner() {
  const { t } = useTranslation()
  const hasResponded = useConsentStore(selectHasRespondedAndValid)
  const acceptAll = useConsentStore((state) => state.acceptAll)
  const refuseAll = useConsentStore((state) => state.refuseAll)
  const openPreferences = useConsentStore((state) => state.openPreferences)

  return (
    <>
      {!hasResponded && (
        <div
          role="region"
          aria-live="polite"
          aria-label={t('cookies.banner_title')}
          className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card shadow-lg motion-safe:animate-in motion-safe:slide-in-from-bottom motion-safe:duration-300"
        >
          <div className="container mx-auto flex flex-col gap-4 px-4 py-4 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <p className="text-sm font-medium text-foreground">{t('cookies.banner_title')}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('cookies.banner_description')}{' '}
                <Link
                  to="/confidentialite"
                  className="underline underline-offset-2 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded"
                >
                  {t('cookies.banner_privacy_link')}
                </Link>
              </p>
            </div>

            {/* Accept/Refuse carry equal visual weight — neither is a faint link. */}
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={openPreferences}>
                {t('cookies.customize')}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={refuseAll}>
                {t('cookies.refuse_all')}
              </Button>
              <Button type="button" size="sm" onClick={acceptAll}>
                {t('cookies.accept_all')}
              </Button>
            </div>
          </div>
        </div>
      )}
      <CookiePreferencesDialog />
    </>
  )
}
